import { ActionBarPrimitive, useAuiState, useMessageTiming } from '@assistant-ui/react';
import { Check, Copy, Pencil } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Button } from './button.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export function formatSpecDuration(ms: number): string {
  if (ms < 60_000) return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/** Total stream time for an assistant message, or nothing when unknown. */
export function ParcelMessageTimingChip() {
  const timing = useMessageTiming();
  if (!timing?.totalStreamTime) return null;
  return (
    <span
      data-slot="assistant-message-timing"
      className="px-1 text-xs tabular-nums text-muted-foreground"
    >
      {formatSpecDuration(timing.totalStreamTime)}
    </span>
  );
}

const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const FULL = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' });

/** "1:20 PM" for today, "Sep 12" otherwise; the full stamp rides on hover. */
export function formatMessageTimestamp(at: Date, now: Date = new Date()): string {
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay ? TIME.format(at) : DAY.format(at);
}

/** When a message was sent, or nothing when the runtime did not say. */
export function ParcelMessageTimestamp() {
  const createdAt = useAuiState((s) => s.message.createdAt);
  if (!(createdAt instanceof Date) || Number.isNaN(createdAt.getTime())) return null;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-slot="assistant-message-timestamp"
            className="px-1 text-xs tabular-nums text-muted-foreground"
          >
            {formatMessageTimestamp(createdAt)}
          </span>
        </TooltipTrigger>
        <TooltipContent>{FULL.format(createdAt)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface ParcelActionBarButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  /** Accessible name and tooltip text. */
  label: string;
}

/**
 * Icon-only action for a message bar: quiet, small, named by its tooltip.
 * Carries its own provider so it also works rendered on its own.
 */
export const ParcelActionBarButton = React.forwardRef<
  HTMLButtonElement,
  ParcelActionBarButtonProps
>(({ label, className, children, ...props }, ref) => (
  <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className={cn(
            'size-7 text-muted-foreground hover:text-foreground [&_svg]:size-3.5',
            className,
          )}
          {...props}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
));
ParcelActionBarButton.displayName = 'ParcelActionBarButton';

/** Copy through a formatter: the message's text parts, joined the way the primitive joins them. */
function ParcelFormattedCopyButton({ formatText }: { formatText: (text: string) => string }) {
  const text = useAuiState((s) =>
    s.message.content.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('\n\n'),
  );
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3_000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <ParcelActionBarButton
      label={copied ? 'Copied' : 'Copy'}
      onClick={() => {
        void navigator.clipboard.writeText(formatText(text)).then(() => setCopied(true));
      }}
    >
      {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
    </ParcelActionBarButton>
  );
}

function ParcelCopyButton({ formatText }: { formatText?: (text: string) => string }) {
  const copied = useAuiState((s) => s.message.isCopied);
  if (formatText) return <ParcelFormattedCopyButton formatText={formatText} />;
  return (
    <ActionBarPrimitive.Copy asChild>
      <ParcelActionBarButton label={copied ? 'Copied' : 'Copy'}>
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
      </ParcelActionBarButton>
    </ActionBarPrimitive.Copy>
  );
}

/** Edit for the thread's last user message, hidden while a run is going or the runtime cannot edit. */
export function ParcelEditButton() {
  const editable = useAuiState((s) => {
    if (s.message.role !== 'user' || s.thread.isRunning || !s.thread.capabilities.edit)
      return false;
    return !s.thread.messages.slice(s.message.index + 1).some((m) => m.role === 'user');
  });
  if (!editable) return null;
  return (
    <ActionBarPrimitive.Edit asChild>
      <ParcelActionBarButton label="Edit">
        <Pencil aria-hidden />
      </ParcelActionBarButton>
    </ActionBarPrimitive.Edit>
  );
}

export interface ParcelAssistantActionBarProps {
  className?: string;
  children?: React.ReactNode;
  /** What Copy writes, for text that holds markup (Spec's file chips). */
  formatCopyText?: (text: string) => string;
  /** An answer's last item, after its time (Spec's sources). */
  end?: React.ReactNode;
}

/**
 * Always mounted, revealed by opacity: mounting on hover (`autohide`) inserted
 * a row into the thread and shifted everything below it. The last message's
 * bar stays visible without hover.
 */
export function ParcelAssistantActionBar({
  className,
  children,
  formatCopyText,
  end,
}: ParcelAssistantActionBarProps) {
  const isAssistant = useAuiState((s) => s.message.role === 'assistant');
  const isLast = useAuiState((s) => s.message.isLast);
  // Order follows reading direction: a user bubble reads time, actions, Copy;
  // an answer reads Copy, actions, timing, time.
  return (
    <TooltipProvider delayDuration={300}>
      <ActionBarPrimitive.Root
        hideWhenRunning
        data-slot="assistant-action-bar"
        data-visible={isLast ? '' : undefined}
        className={cn(
          'flex h-7 items-center gap-0.5 opacity-0 transition-opacity group-hover/message:opacity-100 focus-within:opacity-100 data-[visible]:opacity-100',
          className,
        )}
      >
        {isAssistant ? (
          <ParcelCopyButton formatText={formatCopyText} />
        ) : (
          <ParcelMessageTimestamp />
        )}
        {children}
        {isAssistant ? (
          <>
            <ParcelMessageTimingChip />
            <ParcelMessageTimestamp />
            {end}
          </>
        ) : (
          <ParcelCopyButton formatText={formatCopyText} />
        )}
      </ActionBarPrimitive.Root>
    </TooltipProvider>
  );
}
